<script setup lang="ts">
import {computed, ref} from 'vue'
import {useStore} from 'vuex'
import {toTypedSchema} from '@vee-validate/zod'
import * as z from 'zod'
import {readAndCompressImage} from 'browser-image-resizer'

import {toast} from '@/components/ui/toast'
import {Form, FormControl, FormField, FormItem, FormLabel, FormMessage} from '@/components/ui/form'
import {Input} from '@/components/ui/input'
import {Button} from '@/components/ui/button'
import {Spinner} from '@/components/ui/spinner'
import {Avatar, AvatarImage} from '@/components/ui/avatar'
import {MediaUpload} from '@/components/media'
import OwnerTransfer from './OwnerTransfer.vue'

const store = useStore()

const org = computed(() => store.getters['org/getCurrent'])
const isOwner = computed(() => store.getters['access/isOwner'])
const avatarUpload = ref<{getQueuedFiles: () => {file: File}[], clearQueue: () => void} | null>(null)
const inProgress = ref(false)

const formSchema = toTypedSchema(z.object({
  name: z.string().min(3).max(100)
}))

const resizeAvatar = async (file: File) => {
  const resized = await readAndCompressImage(file, {quality: 0.8, maxWidth: 400, maxHeight: 400})
  if (resized.size / 1024 / 1024 > 1) {
    throw new Error('File size must be less than 1MB')
  }
  return resized
}

const onSubmit = async (values: any) => {
  const orgId = org.value.organization_id
  const avatarFile = avatarUpload.value?.getQueuedFiles()[0]?.file

  inProgress.value = true
  try {
    if (values.name !== org.value.organization_name) {
      await store.dispatch('org/updateOrg', {orgId, organization_name: values.name})
    }
    if (avatarFile) {
      await store.dispatch('org/updateOrgAvatar', {orgId, file: await resizeAvatar(avatarFile)})
      avatarUpload.value?.clearQueue()
    }
    await Promise.all([store.dispatch('org/fetchOrg', orgId), store.dispatch('org/fetchOrgs')])
    toast({title: 'Settings saved'})
  } catch (e: any) {
    toast({title: `Can't save settings. ${e.response?.data?.detail ?? e.message}`, variant: 'destructive'})
  } finally {
    inProgress.value = false
  }
}
</script>

<template>
<div v-if="org">
  <h3 class="text-base font-bold mb-3">General</h3>

  <Form
    class="w-1/2 space-y-6"
    :validation-schema="formSchema"
    :initial-values="{name: org.organization_name}"
    @submit="onSubmit"
  >
    <FormField v-slot="{ componentField }" name="name">
      <FormItem>
        <FormLabel>Name</FormLabel>
        <FormControl>
          <Input v-bind="componentField" />
        </FormControl>
        <FormMessage />
      </FormItem>
    </FormField>

    <div class="space-y-2">
      <div class="text-sm font-medium">Logo</div>
      <Avatar v-if="org.avatar_url" class="w-20 h-20 rounded-lg">
        <AvatarImage :src="org.avatar_url" :alt="org.organization_name" />
      </Avatar>
      <MediaUpload
        ref="avatarUpload"
        class="!max-w-none"
        :allowed-types="['image']"
        :multiple="false"
        :max-files="1"
        defer-upload
        compact
      />
    </div>

    <div class="flex justify-end">
      <Button type="submit" :disabled="inProgress">
        <Spinner v-if="inProgress" class="w-4 h-4" />
        <span v-else>Save</span>
      </Button>
    </div>
  </Form>

  <OwnerTransfer v-if="isOwner" :org-id="org.organization_id" />
</div>
</template>
